import { fmtDate, siteTodayKey, visitWhen } from '@/lib/dates';
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, Calendar, FileCheck, FolderKanban, Info, Loader2, MessageSquareWarning, Pencil, Plus, TrendingUp, UserCog, Users, X } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  workspace, onboarding, studies, subjects, visits, binder, type WorkspaceHubResponse, type StudyLifecycleStage,
} from '@/api/client';
import {
  humanStatus, humanRole, studyStatusBadgeClass,
} from '@/lib/humanise';
import { toast } from 'sonner';
import { ActionMenu, ActionItem } from '@/components/ui/ActionMenu';
import { Lock, FastForward, CheckCircle2, Archive } from 'lucide-react';
import { StudyLockBanner } from '@/pages/studies/StudyLockBanner';
import { ConsentDocumentsPanel } from '@/pages/studies/consent/ConsentDocumentsPanel';
import { BulkReConsentNotifyButton } from '@/pages/consent/BulkReConsentNotifyButton';
import { DoaResponsibilitiesPanel } from '@/pages/studies/DoaResponsibilitiesPanel';
import { ProtocolAmendmentsPanel } from '@/pages/studies/ProtocolAmendmentsPanel';
import { AdverseEventConfigPanel } from '@/pages/studies/AdverseEventConfigPanel';
import { StudyLinksPanel } from '@/pages/studies/StudyPortfolioPage';
import { StudyMilestonesPanel } from '@/pages/studies/StudyMilestonesPanel';
import { StudyDetailsPanel } from '@/pages/studies/StudyDetailsPanel';
import { SponsorDetailCard } from '@/pages/studies/SponsorDetailCard';
import { AuditTrailPanel } from '@/components/audit/AuditTrailPanel';
import { resolveId } from '@/lib/encryptId';
import { useHasRole } from '@/components/RoleGate';
import { isStudyReadOnly } from '@/lib/permissions';
import { AssignStaffDialog } from '@/pages/studies/AssignStaffDialog';
import { useAuth } from '@/contexts/AuthContext';
import { canManageStudyStaff } from '@/lib/staffPermissions';
import { isPrimaryBinder } from '@/lib/binderScope';
import { errorText, errorToneClass, isPermissionError } from '@/lib/permissionError';
import { subjectDisplayId } from '@/lib/subjectLabel';


const VISIT_ACTION_ROLES = [
  'principal_investigator', 'sub_investigator', 'coordinator',
] as const;

/**
 * DREAM-20 — Study Workspace Hub.
 *
 * Per-study landing page reached from the Portfolio. Single composite call
 * to /studies/{id}/workspace returns tiles, time-series, visit panel,
 * team panel, and configured forms. AC12 scope guard enforced server-side
 * (site_id forced from JWT claim).
 */
export function StudyWorkspaceHubPage() {
  const { id: encryptedId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const studyId = encryptedId ? resolveId(encryptedId) : undefined;
  const qc = useQueryClient();
  const { user } = useAuth();
  const [assignStaffOpen, setAssignStaffOpen] = useState(false);

  if (!studyId) return <div className="card p-6 text-sm text-ink-muted">Study id missing.</div>;

  const q = useQuery({
    queryKey: ['workspace', studyId],
    queryFn: () => workspace.get(studyId),
  });

  if (q.isLoading) {
    return (
      <div className="card flex items-center justify-center gap-2 p-10 text-ink-muted">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading workspace…
      </div>
    );
  }
  if (q.isError || !q.data) {
    return (
      <div className={`card flex items-start gap-2 p-4 text-sm ${errorToneClass(q.error)}`}>
        {isPermissionError(q.error) ? <Info className="mt-0.5 h-4 w-4 shrink-0"/> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />}
        <span>{errorText(q.error, 'Failed to load workspace.')}</span>
      </div>
    );
  }

  const data = q.data;
  // Harish, 2 Oct 2026: the team runs an activated study by its role on it;
  // Site Admin / Site Study Designer view it read-only — except its staff,
  // which they add and remove at any stage (Harish, 7 Oct 2026).
  const activated = Boolean(data.study_site.activated_at);
  const readOnly = isStudyReadOnly(user?.role, activated);
  const myStudyRole = data.team_members.find((t) => t.user_id === user?.id)?.role ?? null;
  const canManageStaff = canManageStudyStaff(user?.role, user?.id, data.team_members, activated);

  return (
    <div className="space-y-5">
      <PageHeader
        backTo="/studies"
        title={data.study.title}
        subtitle={
          <p className="mt-0.5 text-[11px] text-ink-muted">
            <span className="font-mono">{data.study.protocol_number}</span> · {data.study.sponsor}
            {data.study.sponsor_study_id ? ` · Sponsor ID: ${data.study.sponsor_study_id}` : ''} ·{' '}
            <span className={studyStatusBadgeClass(data.study_site.status)}>
              {humanStatus(data.study_site.status)}
            </span>
          </p>
        }
        actions={
          // Monitor is a read-only oversight role — every action here
          // (Onboard subject, Edit forms, Open DOA, Manage staff, and the
          // Lifecycle "..." menu: Close enrollment, Lock study, Move to
          // follow-up, Mark completed, Archive study) mutates the study,
          // so none of it belongs in a Monitor's view — nor the three
          // admins', who view studies read-only (Harish, 1 Oct 2026).
          user?.role !== 'monitor' && !readOnly ? (
            <>
              <LifecycleActions
                studyId={studyId}
                currentStage={data.study_site.status as StudyLifecycleStage | string}
                isLocked={Boolean(data.study.locked_at)}
                canTransition={!!myStudyRole && ['principal_investigator', 'sub_investigator', 'coordinator'].includes(myStudyRole)}
                canLock={myStudyRole === 'principal_investigator'}
              />
              <QuickActions
                studyId={studyId}
                navigate={navigate}
                canManageStaff={canManageStaff}
                onManageStaff={() => setAssignStaffOpen(true)}
                doaComplete={data.doa_complete}
              />
            </>
          ) : canManageStaff ? (
            <button
              type="button"
              className="btn-secondary text-xs"
              title="Add or remove this study's staff"
              onClick={() => setAssignStaffOpen(true)}
            >
              <Users className="h-3.5 w-3.5" /> Manage staff
            </button>
          ) : undefined
        }
      />

      <AssignStaffDialog
        studyId={studyId}
        studyLabel={`${data.study.title} — ${data.study.protocol_number}`}
        blindingType={data.study.blinding_type}
        open={assignStaffOpen}
        activated={activated}
        onClose={() => setAssignStaffOpen(false)}
        onAssigned={() => qc.invalidateQueries({ queryKey: ['workspace', studyId] })}
      />

      {/* CTMS-010 — Study Lock banner. Hidden when locked_at is null. */}
      <StudyLockBanner
        studyId={data.study.study_id}
        lockedAt={data.study.locked_at ?? null}
        lockReason={data.study.lock_reason ?? null}
      />

      <HubTiles data={data} />

      {/* Punch-list f8 — post-onboarding edit for the study's non-identity
          fields; protocol_number/title/sponsor stay locked. */}
      <StudyDetailsPanel studyId={studyId} study={data.study} studySite={data.study_site} readOnly={readOnly} />

      {/* Punch-list f11 — sponsor company detail + optional logo. */}
      <SponsorDetailCard sponsorName={data.study.sponsor} />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="h-[280px] lg:col-span-2">
          <EnrollmentChart data={data.enrollment_series} target={data.study.enrollment_target} />
        </div>
        <div className="h-[280px]">
          <VisitSchedulePanel entries={data.visit_panel} studyId={studyId} />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ConfiguredFormsSection forms={data.configured_forms} studyId={studyId} />
        <TeamMembersSection
          members={data.team_members}
          canManageStaff={canManageStaff}
          onManageStaff={() => setAssignStaffOpen(true)}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* Phase 1 item 7 (AC6) — coordinator-triggered bulk re-consent
            notification. Preview → confirm two-step; log-and-continue
            per-subject on send failure. */}
        <BulkReConsentNotifyButton studyId={studyId} readOnly={readOnly} />
      </div>

      <ConsentDocumentsPanel studyId={studyId} readOnly={readOnly} isStudyPi={myStudyRole === 'principal_investigator'} />

      {/* 2026-09-28 — protocol amendment approval workflow. */}
      <ProtocolAmendmentsPanel studyId={studyId} readOnly={readOnly} />

      {/* Phase 1 Item 9 — per-study DoA responsibility subset. */}
      <DoaResponsibilitiesPanel studyId={studyId} readOnly={readOnly} />

      {/* Phase 5 (AC2) — AE configuration surface. */}
      <AdverseEventConfigPanel studyId={studyId} readOnly={readOnly} />

      {/* Punch-list f10 — per-study configurable milestone tracking. */}
      <StudyMilestonesPanel studyId={studyId} readOnly={readOnly} />

      {/* Punch-list b16 — study-scoped links had nowhere to display
          "inside the study"; they only ever showed on the cross-study
          Portfolio dashboard. */}
      <StudyLinksPanel studyId={studyId} readOnly={readOnly} />

      {/* Punch-list f8 — full audit log on every edit, reusing the same
          panel the onboarding wizard already uses. */}
      <div className="card">
        <div className="border-b border-surface-border bg-surface-muted/50 px-4 py-3">
          <h2 className="text-sm font-semibold text-ink-primary">Audit log</h2>
        </div>
        <div className="flex h-[360px] flex-col">
          <AuditTrailPanel scope={{ studyId }} />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Lifecycle action menu — Active → Closed → Follow-up → Completed → Archived.
 * Calls the BE transitionLifecycle endpoint; only valid forward edges are
 * exposed. Adds the "manage the study through every stage" surface that
 * Jawahar's 7-stage workflow comment expects.
 */
/**
 * Lifecycle steps: the PI, SI or Coordinator on the study; Lock: the PI on
 * the study (Harish, 2 Oct 2026). Unlock stays Platform Admin only.
 */
function LifecycleActions({
  studyId,
  currentStage,
  isLocked,
  canTransition,
  canLock,
}: {
  studyId: string;
  currentStage: string;
  isLocked: boolean;
  canTransition: boolean;
  canLock: boolean;
}) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (target: 'closed_to_enrollment' | 'follow_up' | 'completed' | 'archived') =>
      onboarding.transitionLifecycle(studyId, target),
    onSuccess: (_data, target) => {
      toast.success(`Study moved to ${target.replace(/_/g, ' ')}`);
      qc.invalidateQueries({ queryKey: ['workspace', studyId] });
      qc.invalidateQueries({ queryKey: ['portfolio'] });
      qc.invalidateQueries({ queryKey: ['portfolio-summary'] });
    },
    onError: (err) => toast.error(errorText(err, 'Failed to transition')),
  });
  // CTMS-010 — Lock study mutation. Surfaced as a Lifecycle action so
  // the same admin who can move the study forward can also pause it.
  const lockMut = useMutation({
    mutationFn: (reason: string) => studies.lock(studyId, { reason }),
    onSuccess: () => {
      toast.success('Study locked. All data is now read-only.');
      qc.invalidateQueries({ queryKey: ['workspace', studyId] });
      qc.invalidateQueries({ queryKey: ['portfolio'] });
    },
    onError: (err) => toast.error(errorText(err, 'Lock failed')),
  });
  // Forward edges only — see BE FORWARD_EDGES.
  const edges: Partial<Record<string, { target: 'closed_to_enrollment' | 'follow_up' | 'completed' | 'archived'; label: string; icon: React.ElementType; confirm: string }>> = {
    active: { target: 'closed_to_enrollment', label: 'Close enrollment', icon: Lock, confirm: 'Close enrollment for this study? New subjects cannot be added after.' },
    closed_to_enrollment: { target: 'follow_up', label: 'Move to follow-up', icon: FastForward, confirm: 'Move this study to follow-up?' },
    follow_up: { target: 'completed', label: 'Mark completed', icon: CheckCircle2, confirm: 'Mark this study completed?' },
    completed: { target: 'archived', label: 'Archive study', icon: Archive, confirm: 'Archive this study? It will move to the Archived tab.' },
  };
  const next = canTransition ? edges[currentStage] : undefined;
  const showLock = canLock && !isLocked;
  // Show the menu when EITHER a forward edge exists OR a lock action is available.
  if (!next && !showLock) return null;
  return (
    <ActionMenu label="Lifecycle actions">
      {next && (
        <ActionItem
          icon={next.icon}
          onClick={() => {
            if (window.confirm(next.confirm)) mut.mutate(next.target);
          }}
        >
          {next.label}
        </ActionItem>
      )}
      {showLock && (
        <ActionItem
          icon={Lock}
          onClick={() => {
            const reason = window.prompt('Reason for locking this study (3-2000 chars):');
            if (reason && reason.trim().length >= 3) lockMut.mutate(reason.trim());
            else if (reason !== null) toast.error('A reason (3+ chars) is required to lock a study.');
          }}
        >
          Lock study
        </ActionItem>
      )}
    </ActionMenu>
  );
}

function QuickActions({
  studyId,
  navigate,
  canManageStaff,
  onManageStaff,
  doaComplete,
}: {
  studyId: string;
  navigate: (p: string) => void;
  canManageStaff: boolean;
  onManageStaff: () => void;
  doaComplete: boolean;
}) {
  // Training module Phase 6 — per-study DOA quick-link (client: "that
  // should be option for a DOA... I can just go directly to that").
  // The DOA grid lives at /binder/:binderId/doa/grid, keyed by binder
  // not study, so resolve this study's binder from the caller's binder
  // list — same data every other binder-aware page on this study
  // already needs, so it's cheap to share via the query cache.
  // DoA mode: the team reaches its DoA before anyone's row is approved.
  const bindersQ = useQuery({
    queryKey: ['binder', 'list', 'doa'],
    queryFn: () => binder.listForDoa(),
    staleTime: 60_000,
  });
  const studyBinder = bindersQ.data?.binders.find(
    (b) => b.study_id === studyId && isPrimaryBinder(b),
  );

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        className="btn-primary text-xs disabled:opacity-50"
        disabled={!doaComplete}
        title={doaComplete ? undefined : 'Locked until this study\'s DoA version is active — every staff member on it has signed and the PI has countersigned each row.'}
        onClick={() => doaComplete && navigate(`/subjects?study_id=${studyId}`)}
      >
        <Plus className="h-3.5 w-3.5" /> Onboard subject
      </button>
      <button
        type="button"
        className="btn-secondary text-xs"
        onClick={() => navigate(`/forms?study_id=${studyId}`)}
      >
        <Pencil className="h-3.5 w-3.5" /> Edit forms
      </button>
      <button
        type="button"
        className="btn-secondary text-xs disabled:opacity-50"
        disabled={!studyBinder}
        title={studyBinder ? undefined : 'The Delegation of Authority is available once the study is activated.'}
        onClick={() => studyBinder && navigate(`/binder/${studyBinder.binder_id}/doa/grid`)}
      >
        <UserCog className="h-3.5 w-3.5" /> Open DOA
      </button>
      {canManageStaff && (
        <button
          type="button"
          className="btn-secondary text-xs"
          title="Add or remove this study's staff"
          onClick={onManageStaff}
        >
          <Users className="h-3.5 w-3.5" /> Manage staff
        </button>
      )}
    </div>
  );
}

function HubTiles({ data }: { data: WorkspaceHubResponse }) {
  const { tiles } = data;
  const siteTarget = tiles.enrolled.site_target;
  const globalTarget = tiles.enrolled.target;
  // Progress bar uses site target when available, else global
  const barTarget = (siteTarget ?? 0) > 0 ? siteTarget! : globalTarget;
  const enrolledPct = barTarget === 0
    ? 0 : Math.round((tiles.enrolled.count / barTarget) * 100);

  // Display: "3 / 5 (site) · 10 global" — or just "3" when no targets set
  const siteLabel = siteTarget != null && siteTarget > 0 ? `/ ${siteTarget} this site` : null;
  const globalLabel = globalTarget > 0 ? `${globalTarget} global` : null;
  const enrolledPrimary = tiles.enrolled.count.toString();
  const enrolledSub = [siteLabel, globalLabel].filter(Boolean).join(' · ') || 'No target set';

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Tile
        label="Enrolled"
        icon={<Users className="h-4 w-4 text-ink-muted" />}
        primary={enrolledPrimary}
        sub={
          <div className="space-y-1">
            <div className="text-[10px] text-ink-muted">{enrolledSub}</div>
            <ProgressBar pct={enrolledPct} />
          </div>
        }
      />
      <Tile
        label="Open queries"
        icon={<MessageSquareWarning className="h-4 w-4 text-ink-muted" />}
        primary={String(tiles.open_queries.count)}
        sub={
          // Phase 4 extended contract AC-EXT-9 — surface + / − / 0
          // deltas distinctly. Positive (more open than last week) is
          // warning-red; negative (progress made) is success-green;
          // zero is muted. Wired to the real snapshot when the weekly
          // cron has written one; otherwise the heuristic fallback.
          <span
            className={`text-[10px] ${
              tiles.open_queries.weekly_change > 0
                ? 'text-warning-700'
                : tiles.open_queries.weekly_change < 0
                  ? 'text-success-700'
                  : 'text-ink-muted'
            }`}
          >
            {tiles.open_queries.weekly_change > 0
              ? `+${tiles.open_queries.weekly_change} since last week`
              : tiles.open_queries.weekly_change < 0
                ? `${tiles.open_queries.weekly_change} since last week`
                : 'unchanged since last week'}
          </span>
        }
      />
      <Tile
        label="Forms completed"
        icon={<FileCheck className="h-4 w-4 text-ink-muted" />}
        primary={`${tiles.forms_completed.count} / ${tiles.forms_completed.total}`}
        sub={
          <span className="text-[10px] text-ink-muted">
            {tiles.forms_completed.pct}% complete
          </span>
        }
      />
      <Tile
        label="Upcoming visits (7d)"
        icon={<Calendar className="h-4 w-4 text-ink-muted" />}
        primary={String(tiles.upcoming_visits_7d)}
      />
    </div>
  );
}

function Tile({
  label, primary, sub, icon,
}: { label: string; primary: string; sub?: React.ReactNode; icon?: React.ReactNode }) {
  return (
    <div className="card p-4">
      <div className="flex items-center justify-between">
        <div className="eyebrow mb-0.5">{label}</div>
        {icon}
      </div>
      <div className="text-2xl font-bold text-ink-primary">{primary}</div>
      {sub && <div className="mt-1">{sub}</div>}
    </div>
  );
}

function ProgressBar({ pct }: { pct: number }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded bg-surface-muted">
      <div className="h-full bg-accent-500" style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );
}

// ---------------------------------------------------------------------------

function EnrollmentChart({ data, target }:
  { data: WorkspaceHubResponse['enrollment_series']; target: number }) {
  const [tooltip, setTooltip] = useState<{ bucket: string; enrolled: number; xPct: number; yPct: number } | null>(null);

  const rawMax = Math.max(target, 1, ...data.map((p) => p.enrolled));
  // Step=1 for small ranges; for larger ranges pick a "nice" step so there are ~6 ticks.
  // e.g. rawMax=12 → step=2 → ticks 0,2,4,6,8,10,12
  const step = rawMax <= 5
    ? 1
    : (() => {
        const raw = rawMax / 6;
        const mag = Math.pow(10, Math.floor(Math.log10(raw)));
        const norm = raw / mag;
        if (norm <= 1) return mag;
        if (norm <= 2) return 2 * mag;
        if (norm <= 5) return 5 * mag;
        return 10 * mag;
      })();
  // Round max up to nearest step multiple so the top tick lands exactly on max.
  const max = rawMax <= 5 ? rawMax : Math.ceil(rawMax / step) * step;

  const W = 100, H = 40;
  // Top/bottom padding so lines at max/0 never clip at the SVG edge
  const PAD = 4;
  const yFor = (v: number) => PAD + (H - 2 * PAD) * (1 - v / max);

  const pts = data.length === 0 ? [] : data.map((p, i) => {
    const x = data.length === 1 ? W / 2 : (i / (data.length - 1)) * W;
    return { x, y: yFor(p.enrolled), ...p };
  });
  const targetY = yFor(target);
  const baselineY = yFor(0);
  const linePoints = pts.map((p) => `${p.x},${p.y}`).join(' ');
  const areaPoints = pts.length > 0
    ? `${pts[0]!.x},${baselineY} ${linePoints} ${pts[pts.length - 1]!.x},${baselineY}`
    : '';

  // Build Y ticks from 0 → max in `step` increments, then reverse for top-to-bottom rendering.
  const yTicks = (() => {
    const ticks: { value: number; y: number; yPct: number }[] = [];
    for (let v = 0; v <= max; v += step) {
      ticks.push({ value: v, y: yFor(v), yPct: (yFor(v) / H) * 100 });
    }
    return ticks.reverse();
  })();

  const xLabels: { label: string; pct: number }[] = data.length === 0 ? []
    : data.length === 1 ? [{ label: data[0]!.bucket, pct: 50 }]
      : [
        { label: data[0]!.bucket, pct: 0 },
        ...(data.length > 2 ? [{ label: data[Math.floor(data.length / 2)]!.bucket, pct: 50 }] : []),
        { label: data[data.length - 1]!.bucket, pct: 100 },
      ];

  return (
    <div className="card flex h-full flex-col p-4">
      {/* Header + legend */}
      <div className="mb-3 flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-ink-muted" />
          <h2 className="text-sm font-semibold text-ink-primary">Enrollment over time</h2>
          <span className="text-[10px] uppercase tracking-wider text-ink-muted">last 6 months</span>
        </div>
        <div className="ml-auto flex items-center gap-4 text-[10px] text-ink-muted">
          <span className="flex items-center gap-1.5">
            <span className="h-3 w-3 shrink-0 rounded-full bg-accent-500 ring-2 ring-white" />
            Subjects enrolled
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-6 border-t-2 border-dashed border-amber-500" />
            Target ({target})
          </span>
        </div>
      </div>

      {data.length === 0 ? (
        <div className="py-6 text-center text-xs text-ink-muted">
          No enrollment events yet. Once subjects are enrolled, monthly counts appear here.
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col gap-1">
          {/* Plot row: Y-axis and SVG are flex siblings → same height via items-stretch */}
          <div className="flex min-h-0 flex-1 gap-1">
            {/* Y-axis: Subjects label + tick labels (auto-height = plot area height) */}
            <div className="flex shrink-0 gap-1">
              <div className="flex items-center justify-center" style={{ width: 14 }}>
                <span className="text-[9px] leading-none text-ink-muted"
                  style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                  Subjects
                </span>
              </div>
              <div className="relative w-7">
                {yTicks.map(({ value, yPct }) => (
                  <span key={value}
                    className="absolute right-0 -translate-y-1/2 text-right text-[9px] tabular-nums text-ink-muted"
                    style={{ top: `${yPct}%` }}>
                    {value}
                  </span>
                ))}
              </div>
            </div>

            {/* Plot area: fills remaining width, same height as Y-axis via stretch */}
            <div className="relative min-w-0 flex-1">
              <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-full w-full">
                {yTicks.map(({ value, y }) => (
                  <line key={value} x1={0} y1={y} x2={W} y2={y}
                    stroke={value === 0 ? '#9ca3af' : '#e5e7eb'}
                    strokeWidth={value === 0 ? 0.7 : 0.5} />
                ))}
                <line x1={0} y1={targetY} x2={W} y2={targetY} stroke="#f59e0b" strokeWidth="0.7" strokeDasharray="2,1.5" />
                {areaPoints && (
                  <polygon points={areaPoints} fill="currentColor" className="text-accent-100" opacity={0.6} />
                )}
                <polyline fill="none" stroke="currentColor" className="text-accent-500"
                  strokeWidth="1.5" strokeLinejoin="round" points={linePoints} />
              </svg>
              {pts.map((p) => (
                <div key={p.bucket}
                  className="absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 cursor-pointer rounded-full bg-accent-500 ring-2 ring-white transition-transform hover:scale-125"
                  style={{ left: `${(p.x / W) * 100}%`, top: `${(p.y / H) * 100}%` }}
                  onMouseEnter={() => setTooltip({ bucket: p.bucket, enrolled: p.enrolled, xPct: (p.x / W) * 100, yPct: (p.y / H) * 100 })}
                  onMouseLeave={() => setTooltip(null)}
                />
              ))}
              {tooltip && (
                <div
                  className="pointer-events-none absolute z-20 rounded bg-gray-800 px-2.5 py-1.5 text-[10px] leading-4 text-white shadow-lg"
                  style={{ left: `${tooltip.xPct}%`, top: `${tooltip.yPct}%`, transform: 'translate(-50%, calc(-100% - 8px))' }}
                >
                  <div className="font-semibold">{tooltip.bucket}</div>
                  <div className="text-gray-300">{tooltip.enrolled} subject{tooltip.enrolled !== 1 ? 's' : ''} enrolled</div>
                  <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
                </div>
              )}
            </div>
          </div>

          {/* X-axis: spacer (= Y-axis width 14+gap+28=46px) + labels + Month title */}
          <div className="flex shrink-0 gap-1">
            <div className="shrink-0" style={{ width: 46 }} />
            <div className="flex flex-1 flex-col gap-0.5">
              <div className="relative h-3 w-full">
                {xLabels.map(({ label, pct }) => (
                  <span key={label}
                    className="absolute -translate-x-1/2 text-[9px] text-ink-muted"
                    style={{ left: `${pct}%` }}>
                    {label}
                  </span>
                ))}
              </div>
              <div className="text-center text-[9px] text-ink-muted">Month</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function VisitSchedulePanel({ entries, studyId }: { entries: WorkspaceHubResponse['visit_panel']; studyId: string }) {
  const qc = useQueryClient();
  const canAct = useHasRole(VISIT_ACTION_ROLES);
  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState({ subject_id: '', visit_date: siteTodayKey(), reason: '' });

  // Always fetch so the panel list shows the Subject ID (not a raw UUID).
  const subjectsQ = useQuery({
    queryKey: ['subjects-list', studyId],
    queryFn: () => subjects.list({ study_id: studyId, limit: 500 }),
    staleTime: 60_000,
  });
  const subjectMap = useMemo(
    () => new Map((subjectsQ.data?.rows ?? []).map((s) => [s.subject_id, subjectDisplayId(s)])),
    [subjectsQ.data],
  );

  const createMut = useMutation({
    mutationFn: () =>
      visits.createUnscheduled({
        study_id: studyId,
        subject_id: draft.subject_id,
        visit_date: new Date(draft.visit_date).toISOString(),
        reason: draft.reason || undefined,
      }),
    onSuccess: () => {
      toast.success('Unscheduled visit added');
      setShowAdd(false);
      setDraft({ subject_id: '', visit_date: siteTodayKey(), reason: '' });
      qc.invalidateQueries({ queryKey: ['workspace', studyId] });
      qc.invalidateQueries({ queryKey: ['visits-cal'] });
    },
    onError: (err) => toast.error(errorText(err, 'Failed to add visit')),
  });

  return (
    <div className="card flex h-full flex-col p-4">
      <div className="mb-3 flex shrink-0 items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-ink-muted" />
          <h2 className="text-sm font-semibold text-ink-primary">Upcoming visits</h2>
        </div>
        {canAct && (
          <button type="button" className="btn-secondary text-xs" onClick={() => setShowAdd(true)}>
            <Plus className="h-3.5 w-3.5" /> Add Unscheduled
          </button>
        )}
      </div>
      {entries.length === 0 ? (
        <div className="py-6 text-center text-xs text-ink-muted">
          No upcoming visits in the schedule.
        </div>
      ) : (
        <ul className="min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-1">
          {entries.map((v) => (
            <li key={v.visit_id} className="flex items-center justify-between gap-2 text-xs">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-ink-primary truncate">{v.visit_name ?? 'Visit'}</div>
                <div className="text-[10px] text-ink-muted">
                  {subjectsQ.isLoading ? (
                    <span className="inline-block h-2.5 w-16 align-middle animate-pulse rounded bg-surface-muted" />
                  ) : (
                    subjectMap.get(v.subject_id) ?? v.subject_id.slice(0, 8)
                  )}
                  {' · '}
                  {visitWhen(v) ? fmtDate(visitWhen(v)!) : 'unscheduled'}
                </div>
              </div>
              <div className="text-[10px] text-ink-muted whitespace-nowrap">
                {v.nominal_day !== null && v.nominal_day !== undefined
                  ? `Day ${v.nominal_day} ${v.window_minus_days != null || v.window_plus_days != null
                    ? `± ${Math.max(v.window_minus_days ?? 0, v.window_plus_days ?? 0)}`
                    : ''}`
                  : v.visit_type === 'unscheduled' ? 'Unscheduled' : ''}
              </div>
            </li>
          ))}
        </ul>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 !m-0">
          <form
            className="card w-full max-w-md space-y-4 p-6"
            onSubmit={(e) => { e.preventDefault(); if (draft.subject_id) createMut.mutate(); }}
          >
            <div className="flex items-start justify-between">
              <h3 className="text-lg font-semibold">Add Unscheduled Visit</h3>
              <button type="button" onClick={() => setShowAdd(false)}><X className="h-4 w-4" /></button>
            </div>
            <div>
              <label className="label text-xs">Subject *</label>
              <select
                className="input"
                required
                value={draft.subject_id}
                onChange={(e) => setDraft({ ...draft, subject_id: e.target.value })}
                disabled={subjectsQ.isLoading}
              >
                <option value="">
                  {subjectsQ.isLoading ? 'Loading subjects…' : subjectsQ.data?.rows.length === 0 ? 'No subjects enrolled' : '— Select a subject —'}
                </option>
                {(subjectsQ.data?.rows ?? []).map((s) => (
                  <option key={s.subject_id} value={s.subject_id}>{subjectDisplayId(s)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label text-xs">Visit date *</label>
              <input
                className="input"
                type="date"
                required
                value={draft.visit_date}
                onChange={(e) => setDraft({ ...draft, visit_date: e.target.value })}
              />
              <p className="mt-1 text-[10px] text-ink-muted">Defaults to today; change if the visit occurred on another date.</p>
            </div>
            <div>
              <label className="label text-xs">Reason / note (optional)</label>
              <textarea
                className="input min-h-[60px]"
                value={draft.reason}
                onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
                maxLength={500}
                placeholder="e.g. AE follow-up, additional lab draw…"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
              <button
                type="submit"
                className="btn-primary"
                disabled={createMut.isPending || !draft.subject_id}
              >
                {createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Add visit
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Forms (Harish, 2 Oct 2026): the designers build and publish forms — still
 * after activation, the one exception to their read-only study view — and the
 * study team attaches published forms to the visits. Both on the Forms page.
 */
const BUILD_FORM_ROLES = [
  'platform_admin', 'application_admin', 'site_admin', 'site_study_designer', 'regulatory_specialist',
] as const;
const ATTACH_FORM_ROLES = ['principal_investigator', 'sub_investigator', 'coordinator'] as const;

function ConfiguredFormsSection({ forms, studyId }: { forms: WorkspaceHubResponse['configured_forms']; studyId: string }) {
  const navigate = useNavigate();
  const canBuild = useHasRole(BUILD_FORM_ROLES);
  const canAttach = useHasRole(ATTACH_FORM_ROLES);
  return (
    <div className="card p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FolderKanban className="h-4 w-4 text-ink-muted" />
          <h2 className="text-sm font-semibold text-ink-primary">Configured forms</h2>
        </div>
        {(canBuild || canAttach) && (
          <button
            type="button"
            className="btn-secondary text-xs"
            onClick={() => navigate(`/forms?study_id=${encodeURIComponent(studyId)}`)}
            title={canBuild ? 'Build forms and attach them to visits' : 'Attach published forms to this study\'s visits'}
          >
            <Plus className="h-3.5 w-3.5" /> {canBuild ? 'New form' : 'Attach forms'}
          </button>
        )}
      </div>
      {forms.length === 0 ? (
        <div className="py-6 text-center text-xs text-ink-muted">
          No forms configured for this study yet.
        </div>
      ) : (
        <ul className="space-y-1">
          {forms.map((f) => (
            <li key={f.form_id} className="flex items-center justify-between gap-2 rounded border border-surface-border bg-white px-2.5 py-1.5 text-xs">
              <span className="font-medium text-ink-primary truncate">{f.name}</span>
              <span className="text-[10px] text-ink-muted">v{f.version} · {humanStatus(f.status)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TeamMembersSection({
  members,
  canManageStaff,
  onManageStaff,
}: {
  members: WorkspaceHubResponse['team_members'];
  canManageStaff: boolean;
  onManageStaff: () => void;
}) {
  const grouped = useMemo(() => {
    const map = new Map<string, typeof members>();
    for (const m of members) {
      const arr = map.get(m.role) ?? [];
      arr.push(m);
      map.set(m.role, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [members]);
  return (
    <div className="card p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <UserCog className="h-4 w-4 text-ink-muted" />
          <h2 className="text-sm font-semibold text-ink-primary">Team members</h2>
        </div>
        {canManageStaff && (
          <button
            type="button"
            className="text-[11px] font-medium text-accent-700 hover:underline"
            onClick={onManageStaff}
          >
            Manage staff →
          </button>
        )}
      </div>
      {members.length === 0 ? (
        <div className="py-6 text-center text-xs text-ink-muted">
          {canManageStaff ? (
            <>
              No per-study delegations yet.{' '}
              <button
                type="button"
                className="text-accent-700 hover:underline"
                onClick={onManageStaff}
              >
                Assign staff.
              </button>
            </>
          ) : (
            'No per-study delegations yet.'
          )}
        </div>
      ) : (
        <ul className="space-y-3">
          {grouped.map(([role, people]) => (
            <li key={role}>
              <div className="text-[10px] uppercase tracking-wider text-ink-muted mb-1">{humanRole(role)}</div>
              <ul className="space-y-1">
                {people.map((m) => (
                  <li key={m.user_id} className="flex items-center gap-2">
                    <span className="h-6 w-6 rounded-full bg-brand-100 flex items-center justify-center text-[10px] font-semibold text-brand-700 shrink-0">
                      {(m.full_name ?? m.email ?? m.user_id).slice(0, 1).toUpperCase()}
                    </span>
                    <span className="text-xs text-ink-primary font-medium truncate">
                      {m.full_name ?? m.email ?? m.user_id.slice(0, 8)}
                    </span>
                    {m.email && m.full_name && (
                      <span className="text-[10px] text-ink-muted truncate">{m.email}</span>
                    )}
                    {m.active === false && <span className="rounded bg-surface-muted px-1.5 py-0.5 text-[10px] font-medium text-ink-muted shrink-0" title="Deactivated or removed from the site">Inactive</span>}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
